// Turnstile widget — loads the challenge script, renders the widget,
// and calls `onToken` with the response. Transport of the token to the
// server is the caller's responsibility.
//
// Site-key resolution (in order):
//   1. Explicit `siteKey` prop (usually sourced from /api/bootstrap).
//   2. Cloudflare's always-pass test key on localhost so the flow is
//      exercisable in local dev.
//   3. Hard-coded prod key as a last-resort fallback. Our Cloudflare
//      Turnstile site whitelists BOTH `trafficsnapshot.skedsocial.com`
//      and `staging.trafficsnapshot.skedsocial.com`, so this key is
//      safe for either env — callers still prefer `siteKey` for
//      future-proofing in case the envs ever split widgets.
//
// Exposes a visible error state (with retry) when Cloudflare rejects
// the challenge. `onExpired` is separate from `onError` so the caller
// can react to the 5-minute TTL refresh without showing an error UI.

import { useCallback, useEffect, useRef, useState } from "react";

const PROD_SITE_KEY_FALLBACK = "0x4AAAAAAC_YorDmG5Wznl8B";
const LOCAL_TEST_KEY = "1x00000000000000000000AA";

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          // Per the Cloudflare docs, `error-callback` receives a
          // short error code string (e.g. `110200` hostname mismatch).
          // Surfacing it lets us show the user something actionable
          // instead of a generic "security challenge failed".
          "error-callback"?: (errorCode: string) => void;
          "expired-callback"?: () => void;
          theme?: "light" | "dark" | "auto";
          size?: "normal" | "compact" | "flexible";
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId: string) => void;
    };
  }
}

export interface TurnstileProps {
  onToken: (token: string) => void;
  onError?: () => void;
  /** Fired when a previously-valid token expires (5 min TTL). The
   *  widget auto-resets and will produce a fresh token via `onToken`,
   *  but the stale token should be cleared in the caller's state to
   *  block submissions in the tiny window between reset + re-challenge.
   *  Kept separate from `onError` so the caller doesn't show an error
   *  UI on a normal TTL event. */
  onExpired?: () => void;
  /** Cloudflare Turnstile site key, sourced from /api/bootstrap so
   *  each env can configure its own. Falls back to the localhost test
   *  key / hard-coded prod key when absent. */
  siteKey?: string | null;
  className?: string;
}

const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js";

function resolveSiteKey(provided: string | undefined | null): string {
  if (provided) return provided;
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    if (host === "localhost" || host === "127.0.0.1") return LOCAL_TEST_KEY;
  }
  return PROD_SITE_KEY_FALLBACK;
}

// Time budget for the script to load once the tag is in the DOM. Past
// this, we reject so the caller's onError fires instead of the promise
// hanging forever (which leaves the widget invisible and the Connect
// button permanently disabled).
const SCRIPT_LOAD_TIMEOUT_MS = 10_000;

function ensureScriptLoaded(): Promise<void> {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  const existing = document.querySelector<HTMLScriptElement>(`script[src^="${SCRIPT_URL}"]`);
  if (existing) {
    // The script tag already exists — either it's still loading, or it
    // finished before this component mounted. Polling window.turnstile
    // covers the already-loaded case; the listeners cover the still-
    // loading case; the timeout covers the errored-and-never-retried
    // case where the error event fired before we could attach a handler.
    return new Promise((resolve, reject) => {
      if (window.turnstile) {
        resolve();
        return;
      }
      let settled = false;
      const finish = (ok: boolean) => {
        if (settled) return;
        settled = true;
        window.clearTimeout(timeoutId);
        existing.removeEventListener("load", onLoad);
        existing.removeEventListener("error", onError);
        if (ok) resolve();
        else reject(new Error("turnstile script failed"));
      };
      const onLoad = () => finish(true);
      const onError = () => finish(false);
      existing.addEventListener("load", onLoad);
      existing.addEventListener("error", onError);
      const timeoutId = window.setTimeout(
        () => finish(Boolean(window.turnstile)),
        SCRIPT_LOAD_TIMEOUT_MS,
      );
    });
  }
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.addEventListener("load", () => resolve());
    script.addEventListener("error", () => reject(new Error("turnstile script failed")));
    document.head.appendChild(script);
  });
}

export function Turnstile({ onToken, onError, onExpired, siteKey, className }: TurnstileProps) {
  const ref = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const [status, setStatus] = useState<"loading" | "ready" | "error">("loading");
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  const render = useCallback(() => {
    setStatus("loading");
    setErrorCode(null);
    let cancelled = false;
    ensureScriptLoaded()
      .then(() => {
        if (cancelled || !ref.current || !window.turnstile) return;
        widgetIdRef.current = window.turnstile.render(ref.current, {
          sitekey: resolveSiteKey(siteKey),
          callback: (token: string) => {
            setStatus("ready");
            setErrorCode(null);
            onToken(token);
          },
          "error-callback": (code) => {
            setStatus("error");
            setErrorCode(code ?? null);
            onError?.();
          },
          "expired-callback": () => {
            // Tokens are single-use with a 5-minute TTL. Per the
            // Cloudflare docs, reset the widget to grant a fresh
            // challenge. `onExpired` (not `onError`) so the caller
            // can clear the stale token without showing an error UI.
            if (widgetIdRef.current && window.turnstile) {
              window.turnstile.reset(widgetIdRef.current);
            }
            onExpired?.();
          },
          theme: "light",
          size: "flexible",
        });
      })
      .catch(() => {
        setStatus("error");
        setErrorCode("script-load");
        onError?.();
      });
    return () => {
      cancelled = true;
      if (widgetIdRef.current && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current);
        widgetIdRef.current = null;
      }
    };
  }, [onToken, onError, onExpired, siteKey]);

  useEffect(() => {
    // `attempt` is a retry tick — bumping it re-runs this effect so the
    // error-retry button can reset the widget even when nothing else
    // changed. `render` is memoised on the real deps.
    void attempt;
    return render();
  }, [render, attempt]);

  return (
    <div className={className}>
      <div ref={ref} aria-label="Security challenge" />
      {status === "error" ? (
        <div
          role="alert"
          className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
        >
          <p>
            Security challenge failed.{" "}
            {errorCode ? (
              <>
                Cloudflare returned{" "}
                <code className="rounded bg-red-100 px-1 py-0.5 font-mono text-[11px]">
                  {errorCode}
                </code>
                .
              </>
            ) : null}
          </p>
          <button
            type="button"
            onClick={() => setAttempt((n) => n + 1)}
            className="mt-1 inline-flex items-center gap-1.5 font-semibold text-red-800 hover:underline"
          >
            <i className="fa-solid fa-rotate-right text-[11px]" aria-hidden="true" />
            Retry challenge
          </button>
        </div>
      ) : null}
    </div>
  );
}
